import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.svg";
import logoCG from "../assets/Logo_CG_blackV.svg";
import chatIcon from "../assets/header/chatImage.svg";
import profileIcon from "../assets/header/profileImage.svg";
import moonIcon from "../assets/header/themes/moon.svg";
import sunIcon from "../assets/header/themes/sun.svg";
import flagEN from "../assets/header/flags/Us.svg";
import flagFR from "../assets/header/flags/Fr.svg";

const MobileSiteHeader = ({
  theme = "dark",
  lang = "en",
  allowFrench = true,
  onToggleTheme,
  onToggleLang,
  token,
  pendingMatchesCount = 0,
  isGuruDomain = false,
  isSamsungBrowser = false,
  onOpenAuthModal,          // ← открывает AuthModal внутри EventDetailPage (только на этой странице)
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = theme === "dark";
  const accent = isGuruDomain ? "#88712B" : (isDark ? "#667eea" : "#4a4a8a");
  const navAccent = isGuruDomain ? "#88712B" : (isDark ? "#39206f" : "#2a2a5a");
  const navBackground = isDark ? "rgba(128, 128, 128, 0.2)" : "rgba(74, 74, 138, 0.1)";
  const glassBackground = isDark
    ? "rgba(30, 40, 70, 0.38)"
    : "rgba(255, 255, 255, 0.52)";
  const glassShadow = isDark
    ? "0 8px 32px rgba(0,0,0,0.38), inset 0 1px 1px rgba(255,255,255,0.07), inset 0 -1px 1px rgba(0,0,0,0.18)"
    : "0 8px 32px rgba(0,0,0,0.18), inset 0 1px 1px rgba(255,255,255,0.12), inset 0 -1px 1px rgba(0,0,0,0.08)";
  const headerHeight = "50px";
  const sidePadding = "4px";
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const hasToken = !!token;
  const effectiveLang = allowFrench ? lang : "en";
  const t = effectiveLang === 'fr'
    ? { login: 'Connexion', register: 'Inscription' }
    : { login: 'Sign In', register: 'Sign Up' };

  const currentLogo = isGuruDomain ? logoCG : logo;

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const minViewport = 320;
  const maxViewport = 400;
  const tWidth = Math.min(Math.max((viewportWidth - minViewport) / (maxViewport - minViewport), 0), 1);
  const lerp = (a, b) => a + (b - a) * tWidth;

  const navButtonWidth = `${lerp(60, 80)}px`;
  const navButtonHeight = `${lerp(24, 28)}px`;
  const controlsGap = `${lerp(4, 8)}px`;
  const logoSize = lerp(24, 28);
  const logoMargin = `0 ${lerp(8, 12)}px`;
  const controlsPaddingRight = `${lerp(8, 12)}px`;
  const buttonFontSize = `${lerp(10, 11)}px`;
  const navButtonPadding = viewportWidth < 360 ? "0 6px" : "0 8px";
  const iconSize = lerp(16, 20);

  const toggleButtonStyle = {
    width: `${lerp(36, 44)}px`,
    height: `${lerp(22, 26)}px`,
    cursor: "pointer",
    borderRadius: "999px",
    background: "transparent",
    border: `1px solid ${accent}`,
    color: accent,
    transition: "all 0.22s ease",
    boxShadow: "none",
    fontSize: buttonFontSize,
    padding: "0",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    WebkitTapHighlightColor: "transparent",
    outline: "none",
  };

  const langButtonStyle = { ...toggleButtonStyle };

  const authButtonStyle = {
    height: `${lerp(20, 24)}px`,
    cursor: "pointer",
    borderRadius: "30px",
    background: "transparent",
    border: `1px solid ${accent}`,
    color: accent,
    transition: "all 0.22s ease",
    boxShadow: "none",
    fontSize: buttonFontSize,
    padding: viewportWidth < 360 ? "2px 6px" : "4px 8px",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    WebkitTapHighlightColor: "transparent",
    outline: "none",
  };

  const navBorderColor = isGuruDomain ? accent : navAccent;

  const navButtonBaseStyle = {
    width: navButtonWidth,
    height: navButtonHeight,
    cursor: "pointer",
    borderRadius: "0px",
    border: "none",
    borderLeft: `1px solid ${navBorderColor}`,
    borderRight: `1px solid ${navBorderColor}`,
    transition: "all 0.2s ease",
    fontSize: buttonFontSize,
    padding: navButtonPadding,
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    WebkitTapHighlightColor: "transparent",
    outline: "none",
  };

  const isChatPage = location.pathname === "/chat" || location.pathname.startsWith("/chat/");
  const isProfilePage = location.pathname === "/profile";
  const isEventDetailPage = /^\/events\/[^/]+$/.test(location.pathname);

  const iconFilter = isGuruDomain
    ? "brightness(0) saturate(100%) invert(42%) sepia(55%) saturate(600%) hue-rotate(5deg) brightness(80%)"
    : "none";
  const themeIconFilter = isGuruDomain
    ? iconFilter
    : isDark
      ? "brightness(0) saturate(100%) invert(65%) sepia(90%) saturate(900%) hue-rotate(5deg) brightness(105%)"
      : "brightness(0) saturate(100%) invert(70%) sepia(30%) saturate(400%) hue-rotate(200deg) brightness(80%)";

  return (
    <header
      style={{
        height: headerHeight,
        background: glassBackground,
        backdropFilter: "blur(16px) saturate(180%)",
        WebkitBackdropFilter: "blur(16px) saturate(180%)",
        boxShadow: glassShadow,
        color: accent,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: `0 ${sidePadding}`,
        boxSizing: "border-box",
        width: "100%",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        border: "none",
      }}
    >
      <img
        src={currentLogo}
        alt="Logo"
        onClick={() => navigate("/")}
        style={{ height: isGuruDomain ? `${logoSize * 2.2}px` : `${logoSize}px`, margin: logoMargin, cursor: "pointer" }}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: controlsGap,
          paddingRight: controlsPaddingRight,
          flexWrap: "nowrap",
        }}
      >
        {/* Кнопка темы — скрыта в Samsung Browser (тема уже зафиксирована на light) */}
        {!isSamsungBrowser && (
          <button style={toggleButtonStyle} onClick={onToggleTheme}>
            <img
              src={isDark ? sunIcon : moonIcon}
              alt={isDark ? "Switch to light" : "Switch to dark"}
              style={{ height: `${lerp(18, 22)}px`, width: `${lerp(18, 22)}px`, filter: themeIconFilter }}
            />
          </button>
        )}

        {/* Переключатель языка — скрываем на connectguru.ai */}
        {!isGuruDomain && (
          <div style={{ position: "relative", display: "inline-block" }}>
            <button
              style={langButtonStyle}
              onClick={() => setIsLangOpen(!isLangOpen)}
            >
              <img
                src={effectiveLang === "fr" ? flagFR : flagEN}
                alt={effectiveLang === "fr" ? "FR" : "EN"}
                style={{ height: "15px", width: "22px", objectFit: "cover", borderRadius: "2px" }}
              />
            </button>

            {isLangOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  minWidth: "130px",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: isDark
                    ? "0 12px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3)"
                    : "0 12px 32px rgba(74,74,138,0.18), 0 2px 8px rgba(0,0,0,0.08)",
                  background: isDark ? "rgba(20,22,42,0.96)" : "rgba(252,252,255,0.97)",
                  backdropFilter: "blur(16px)",
                  border: `1px solid ${accent}30`,
                  zIndex: 1001,
                  padding: "6px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                }}
              >
                {[
                  { value: "en", flag: flagEN, label: "English" },
                  ...(allowFrench ? [{ value: "fr", flag: flagFR, label: "Français" }] : []),
                ].map((opt) => {
                  const isActive = opt.value === effectiveLang;
                  return (
                    <div
                      key={opt.value}
                      onClick={() => {
                        if (!isActive) onToggleLang(opt.value);
                        setIsLangOpen(false);
                      }}
                      style={{
                        padding: "8px 10px",
                        fontSize: buttonFontSize,
                        color: isActive ? accent : (isDark ? "rgba(255,255,255,0.75)" : "rgba(40,40,80,0.75)"),
                        cursor: "pointer",
                        borderRadius: "8px",
                        background: isActive ? `${accent}18` : "transparent",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontWeight: isActive ? 600 : 400,
                        transition: "background 0.15s, color 0.15s",
                      }}
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = `${accent}10`; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                    >
                      <img
                        src={opt.flag}
                        alt={opt.label}
                        style={{ height: "15px", width: "22px", objectFit: "cover", borderRadius: "3px", flexShrink: 0 }}
                      />
                      <span>{opt.label}</span>
                      {isActive && (
                        <span style={{
                          marginLeft: "auto",
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: accent,
                          flexShrink: 0,
                        }} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {hasToken ? (
          <>
            <button
              style={{
                ...navButtonBaseStyle,
                background: isChatPage ? (isGuruDomain ? navBackground : `${accent}18`) : navBackground,
                borderBottom: isChatPage ? `2px solid ${accent}` : "none",
                paddingBottom: isChatPage ? "2px" : "0",
                fontWeight: isChatPage ? 600 : 400,
                color: isChatPage ? accent : (isDark ? "#ffffff" : "#4a4a8a"),
              }}
              onClick={() => navigate("/chat")}
            >
              <img
                src={chatIcon}
                alt="Chat"
                style={{ height: `${iconSize}px`, width: `${iconSize}px`, marginRight: "4px", filter: iconFilter }}
              />
              Chat
            </button>

            <button
              style={{
                ...navButtonBaseStyle,
                background: isProfilePage ? (isGuruDomain ? navBackground : `${accent}18`) : navBackground,
                borderBottom: isProfilePage ? `2px solid ${accent}` : "none",
                paddingBottom: isProfilePage ? "2px" : "0",
                fontWeight: isProfilePage ? 600 : 400,
                color: isProfilePage ? accent : (isDark ? "#ffffff" : "#4a4a8a"),
                position: "relative",
              }}
              onClick={() => navigate("/profile")}
            >
              <img
                src={profileIcon}
                alt="Profile"
                style={{ height: `${iconSize}px`, width: `${iconSize}px`, marginRight: "4px", filter: iconFilter }}
              />
              Profile
              {pendingMatchesCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: "#0abf30",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: "bold",
                    boxShadow: "0 0 6px rgba(10, 191, 48, 0.5)",
                    zIndex: 1001,
                  }}
                >
                  {pendingMatchesCount > 9 ? "9+" : pendingMatchesCount}
                </span>
              )}
            </button>
          </>
        ) : (
          <>
            <button
              style={{ ...authButtonStyle, width: `${lerp(60, 72)}px` }}
              onClick={() => {
                if (isEventDetailPage && onOpenAuthModal) {
                  onOpenAuthModal();
                } else {
                  navigate("/auth");
                }
              }}
            >
              {t.login}
            </button>

            <button
              style={{
                ...authButtonStyle,
                width: `${lerp(70, 84)}px`,
                ...(isSamsungBrowser
                  ? {
                      background: "rgba(220,222,248,0.92)",
                      color: "#3730a3",
                      border: "none",
                      boxShadow: "0 2px 8px rgba(99,102,241,0.15)",
                    }
                  : {
                      background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
                      color: "#ffffff",
                      border: "none",
                      boxShadow: "0 3px 10px rgba(102, 126, 234, 0.3)",
                    }),
              }}
              onClick={() => {
                if (isEventDetailPage && onOpenAuthModal) {
                  onOpenAuthModal();
                } else {
                  navigate("/register");
                }
              }}
            >
              {t.register}
            </button>
          </>
        )}
      </div>
    </header>
  );
};

export default MobileSiteHeader;
